/**
 * Which sound device a player plays to (core/audioCache). Shown only where the
 * webview can do it -- Windows, and Chromium browsers; on Linux and macOS the
 * desktop app plays everything on the system's output, and says so.
 */

import { useEffect, useState } from "react";
import { canChooseOutput, outputDevice, outputDevices, setOutputDevice, type OutputDevice } from "../core/audioCache";
import { toast } from "./Toast";

export function OutputPicker({ name, label }: { name: string; label: string }) {
  const [devices, setDevices] = useState<OutputDevice[]>([]);
  const [named, setNamed] = useState(true);
  const [chosen, setChosen] = useState(() => outputDevice(name));
  const load = (ask = false) => void outputDevices(ask).then((r) => {
    setDevices(r.devices);
    setNamed(r.named);
  });
  useEffect(() => {
    load();
    const md = navigator.mediaDevices;
    const changed = () => load();
    md?.addEventListener?.("devicechange", changed);
    return () => md?.removeEventListener?.("devicechange", changed);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canChooseOutput()) {
    return <span className="nv-dim">This system plays everything on its own sound output. Choose it in the system's sound settings.</span>;
  }
  return (
    <span className="nv-output-picker">
      <select
        className="nv-select"
        aria-label={label}
        value={devices.some((d) => d.id === chosen) ? chosen : ""}
        onChange={async (e) => {
          const id = e.target.value;
          setChosen(id);
          if (!(await setOutputDevice(name, id))) toast("That device could not be used; playing on the system default.", "bad");
        }}
      >
        <option value="">System default</option>
        {devices.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
      </select>
      {!named && devices.length > 0 && (
        <button className="nv-btn sm" onClick={() => load(true)} title="The system shows device names once Nova may use a microphone">Show names</button>
      )}
    </span>
  );
}
